import { useQueryClient } from "@tanstack/react-query";
import useUserFavoriteTokenMutation from "@/src/hooks/favorites/useUserFavoriteTokenMutation";
import { useToastContext } from "@/src/components/Toast/ToastProvider";
import { useCurrentUser } from "@/libs/apis/onboarding/useUser";
import { FAVORITES } from "@/libs/apis/onboarding/reactQueryCacheKeys";
import { PostUserFavorites } from "@/src/hooks/favorites/useUserFavoriteTokens";
import { getFavoritesErrorToastComponent } from "../helpers";
import { PAGE_TYPE } from "@/libs/analytics/types";

export type UseFavoriteTokenMutationProps = Omit<
  PostUserFavorites,
  "tokenIds" | "swooshHandle"
> & {
  tokenId: string;
};

const ACTION_REMOVE = "remove";
const ACTION_ADD = "add";

type UserFavoriteTokensQueryData = Record<string, boolean>;

//TODO: NVS-3014 Consolidate this query with useFavoriteToggle and clean up favorites
const useCurrentUserFavoriteMutation = ({
  chainId,
  tokenId,
  contractAddress,
  keys,
}: UseFavoriteTokenMutationProps) => {
  const { currentUser } = useCurrentUser();
  const { createToast } = useToastContext();
  const queryClient = useQueryClient();
  const favoriteController = useUserFavoriteTokenMutation(
    currentUser?.swooshHandle as string,
  );
  const hasRequiredData = !!tokenId && !!chainId && !!contractAddress;

  const toggleUserFavorite = (isFavorited: boolean) => {
    const previousIsFavorited = isFavorited;
    const nextIsFavorited = !isFavorited;

    // optimistic update to the query data
    queryClient.setQueryData<UserFavoriteTokensQueryData>(
      keys ?? [currentUser?.swooshHandle, FAVORITES],
      (oldData) => {
        const newData = { ...oldData };
        newData[tokenId] = nextIsFavorited; // update the favorite by token id
        return newData;
      },
    );

    favoriteController.mutate(
      {
        tokenId: tokenId,
        chainId: chainId,
        contractAddress: contractAddress,
        action: previousIsFavorited ? ACTION_REMOVE : ACTION_ADD,
      },

      {
        onError() {
          // Undo optimistic update
          queryClient.setQueryData<UserFavoriteTokensQueryData>(
            keys ?? [currentUser?.swooshHandle, FAVORITES],
            (oldData) => {
              const newData = { ...oldData };
              newData[tokenId] = previousIsFavorited;
              return newData;
            },
          );
          createToast({
            ToastComponent: getFavoritesErrorToastComponent(
              !!currentUser?.swooshHandle,
              PAGE_TYPE.profile,
            ),
            theme: "light",
            type: "error",
            autoClose: false,
          });
        },
      },
    );
  };

  return {
    toggleUserFavorite: hasRequiredData ? toggleUserFavorite : undefined,
  };
};

export default useCurrentUserFavoriteMutation;
